import { useMemo } from "react";
import { Grid, Paper, Stack, Typography, styled } from "@mui/material";
import { RepairItem } from "types/restarter";
import AnimatedCounter from "components/core/AnimatedNumber";
import HardwareIcon from "@mui/icons-material/Hardware";
import ScaleIcon from "@mui/icons-material/Scale";
import SavingsIcon from "@mui/icons-material/Savings";
import { isNumber } from "lodash";

type Props = {
  repairItems: RepairItem[];
};

const ImpactPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  padding: theme.spacing(2),
}));

const RestarterImpactItems = ({ repairItems }: Props) => {
  const { fixedItemsCount, totalWeightSaved, totalValueSaved } = useMemo(() => {
    const fixedItems = repairItems.filter((item) => item.status === "FIXED");
    const totalWeightSaved = fixedItems.reduce(
      (acc, item) =>
        acc +
        parseFloat(
          (isNumber(item.weight) ? item.weight.toString() : item.weight) || "0"
        ),
      0
    );
    const totalValueSaved = fixedItems.reduce(
      (acc, item) =>
        acc +
        parseInt(
          (isNumber(item.priceAsNew)
            ? item.priceAsNew.toString()
            : item.priceAsNew) || "0"
        ),
      0
    );
    return {
      fixedItemsCount: fixedItems.length,
      totalWeightSaved,
      totalValueSaved,
    };
  }, [repairItems]);

  return (
    <Grid container spacing={3} sx={{ my: 2 }}>
      <Grid item xs={12} md={4}>
        <ImpactPaper elevation={5}>
          <Stack alignItems="center" justifyContent="center" gap={2}>
            <HardwareIcon color="success" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              Total Items Fixed
            </Typography>
            <Typography color="success.main" variant="h5">
              <AnimatedCounter value={fixedItemsCount} />
            </Typography>
          </Stack>
        </ImpactPaper>
      </Grid>
      <Grid item xs={12} md={4}>
        <ImpactPaper elevation={5}>
          <Stack alignItems="center" justifyContent="center" gap={2}>
            <ScaleIcon color="success" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              E-waste Saved (KG)
            </Typography>
            <Typography color="success.main" variant="h5">
              <AnimatedCounter value={totalWeightSaved} />
            </Typography>
          </Stack>
        </ImpactPaper>
      </Grid>
      <Grid item xs={12} md={4}>
        <ImpactPaper elevation={5}>
          <Stack alignItems="center" justifyContent="center" gap={2}>
            <SavingsIcon color="success" fontSize="large" />
            <Typography variant="body2" color="text.secondary">
              Original Value of Fixed Items (KR)
            </Typography>
            <Typography color="success.main" variant="h5">
              <AnimatedCounter value={totalValueSaved} />
            </Typography>
          </Stack>
        </ImpactPaper>
      </Grid>
    </Grid>
  );
};

export default RestarterImpactItems;
